import RecordManager from "@/components/RecordManager";
import { ImageThumb } from "@/components/ImageField";
import { Heart, Building2, UtensilsCrossed, Clock, Luggage, Palette, Images, Music2, Gift, Plane, Contact } from "lucide-react";
import { useWedding } from "@/context/WeddingContext";
import { formatMoney } from "@/lib/format";

const money = (cur) => (c, it) => (["quote", "deposit", "amount", "price_per_person", "venue_fee", "catering_cost", "bar_cost", "minimum_spend"].includes(c.key) ? formatMoney(it[c.key], cur) : undefined);

export function Party() {
  return <RecordManager entity="party" title="Wedding Party" subtitle="Your inner circle — roles, outfits and responsibilities." icon={Heart} emptyLabel="Add member"
    fields={[
      { key: "name", label: "Name" },
      { key: "role", label: "Role", type: "select", options: ["Bridesmaid", "Groomsman", "Maid/Man of Honour", "Best Man", "Parent", "Flower Girl", "Ring Bearer", "Other"] },
      { key: "contact", label: "Contact" },
      { key: "outfit", label: "Outfit" },
      { key: "accessories", label: "Accessories" },
      { key: "responsibilities", label: "Responsibilities", full: true, type: "textarea" },
      { key: "gift", label: "Gift" },
      { key: "notes", label: "Notes", full: true, type: "textarea" },
    ]}
    columns={[{ key: "name", label: "Name" }, { key: "role", label: "Role" }, { key: "contact", label: "Contact" }, { key: "outfit", label: "Outfit" }]} />;
}

export function Venues() {
  const { wedding } = useWedding(); const cur = wedding?.currency || "AUD";
  return <RecordManager entity="venues" title="Venue Comparison" subtitle="Compare venues side by side and mark your favourite." icon={Building2} emptyLabel="Add venue"
    renderCell={money(cur)}
    fields={[
      { key: "name", label: "Venue name" },
      { key: "location", label: "Location" },
      { key: "capacity", label: "Capacity", type: "number" },
      { key: "venue_fee", label: "Venue fee", type: "number" },
      { key: "catering_cost", label: "Catering cost", type: "number" },
      { key: "minimum_spend", label: "Minimum spend", type: "number" },
      { key: "ceremony_included", label: "Ceremony included", type: "select", options: ["Yes", "No"] },
      { key: "reception_included", label: "Reception included", type: "select", options: ["Yes", "No"] },
      { key: "parking", label: "Parking", type: "select", options: ["Yes", "No", "Limited"] },
      { key: "curfew", label: "Curfew" },
      { key: "preferred", label: "Preferred", type: "select", options: ["Yes", "No"] },
      { key: "pros", label: "Pros", full: true, type: "textarea" },
      { key: "cons", label: "Cons", full: true, type: "textarea" },
      { key: "website", label: "Website" },
      { key: "notes", label: "Notes", full: true, type: "textarea" },
    ]}
    columns={[{ key: "name", label: "Venue" }, { key: "location", label: "Location" }, { key: "capacity", label: "Capacity" }, { key: "venue_fee", label: "Fee" }, { key: "preferred", label: "Preferred" }]} />;
}

export function Food() {
  const { wedding } = useWedding(); const cur = wedding?.currency || "AUD";
  return <RecordManager entity="food_options" title="Food & Drinks" subtitle="Compare menus and drink packages, and see totals per option." icon={UtensilsCrossed} emptyLabel="Add option"
    renderCell={(c, it) => {
      if (["price_per_person", "bar_cost"].includes(c.key)) return formatMoney(it[c.key], cur);
      if (c.key === "total") return formatMoney((Number(it.price_per_person) || 0) * (Number(it.guests) || 0) + (Number(it.bar_cost) || 0), cur);
      return undefined;
    }}
    fields={[
      { key: "caterer", label: "Caterer" },
      { key: "menu_option", label: "Menu option" },
      { key: "price_per_person", label: "Price per person", type: "number" },
      { key: "guests", label: "Number of guests", type: "number" },
      { key: "drinks_package", label: "Drinks package" },
      { key: "bar_cost", label: "Bar cost", type: "number" },
      { key: "dessert", label: "Dessert" },
      { key: "cake", label: "Cake" },
      { key: "dietary", label: "Dietary requirements", full: true },
      { key: "notes", label: "Notes", full: true, type: "textarea" },
    ]}
    columns={[{ key: "caterer", label: "Caterer" }, { key: "menu_option", label: "Menu" }, { key: "price_per_person", label: "Per person" }, { key: "guests", label: "Guests" }, { key: "total", label: "Est. total" }]} />;
}

export function Itinerary() {
  return <RecordManager entity="itinerary" title="Wedding-Day Itinerary" subtitle="A minute-by-minute run sheet for the big day." icon={Clock} emptyLabel="Add event"
    fields={[
      { key: "time", label: "Time" },
      { key: "event", label: "Event" },
      { key: "location", label: "Location" },
      { key: "person", label: "Person responsible" },
      { key: "vendor", label: "Vendor" },
      { key: "notes", label: "Notes", full: true, type: "textarea" },
    ]}
    columns={[{ key: "time", label: "Time" }, { key: "event", label: "Event" }, { key: "location", label: "Location" }, { key: "person", label: "Responsible" }]} />;
}

export function Packing() {
  return <RecordManager entity="packing" title="Packing Lists" subtitle="Everything to bring — for the day, the party and the honeymoon." icon={Luggage} emptyLabel="Add item"
    renderCell={(c, it) => (c.key === "packed" ? (it.packed ? "Packed" : "To pack") : undefined)}
    defaults={{ category: "Wedding", quantity: 1 }}
    fields={[
      { key: "item", label: "Item" },
      { key: "category", label: "List", type: "select", options: ["Wedding", "Partner 1", "Partner 2", "Wedding Party", "Emergency Kit", "Decor", "Reception", "Honeymoon"] },
      { key: "quantity", label: "Quantity", type: "number" },
      { key: "packed", label: "Packed", type: "checkbox" },
      { key: "notes", label: "Notes", full: true, type: "textarea" },
    ]}
    columns={[{ key: "item", label: "Item" }, { key: "category", label: "List" }, { key: "quantity", label: "Qty" }, { key: "packed", label: "Status" }]} />;
}

export function Theme() {
  return <RecordManager entity="theme" title="Theme Board" subtitle="Capture your vision — style, palette and inspiration images." icon={Palette} emptyLabel="Add note"
    renderCell={(c, it) => (c.key === "image_url" ? <ImageThumb value={it.image_url} /> : undefined)}
    fields={[
      { key: "category", label: "Category", type: "select", options: ["Wedding style", "Colour palette", "Flowers", "Dress", "Decor", "Ceremony", "Reception", "Cake", "Stationery", "Other"] },
      { key: "title", label: "Title" },
      { key: "image_url", label: "Inspiration image", full: true, type: "image" },
      { key: "notes", label: "Notes", full: true, type: "textarea" },
    ]}
    columns={[{ key: "image_url", label: "Image" }, { key: "category", label: "Category" }, { key: "title", label: "Title" }, { key: "notes", label: "Notes" }]} />;
}

export function Gallery() {
  return <RecordManager entity="gallery" title="Photo Gallery" subtitle="Your private inspiration gallery — visible only to you." icon={Images} emptyLabel="Add image"
    renderCell={(c, it) => (c.key === "image_url" ? <ImageThumb value={it.image_url} /> : undefined)}
    fields={[
      { key: "title", label: "Title" },
      { key: "category", label: "Category", type: "select", options: ["Dress", "Flowers", "Decor", "Venue", "Cake", "Stationery", "Beauty", "Photography", "Reception", "Ceremony", "Other"] },
      { key: "image_url", label: "Image", full: true, type: "image" },
      { key: "notes", label: "Notes", full: true, type: "textarea" },
    ]}
    columns={[{ key: "image_url", label: "Image" }, { key: "title", label: "Title" }, { key: "category", label: "Category" }]} />;
}

export function Music() {
  return <RecordManager entity="music" title="Music Planner" subtitle="Build your soundtrack for every moment of the day." icon={Music2} emptyLabel="Add song"
    renderCell={(c, it) => (["must_play", "do_not_play"].includes(c.key) ? (it[c.key] ? "Yes" : "—") : undefined)}
    fields={[
      { key: "section", label: "Section", type: "select", options: ["Ceremony", "Cocktail Hour", "Reception", "First Dance", "Parent Dance", "Dinner", "Party", "Last Song"] },
      { key: "song", label: "Song" },
      { key: "artist", label: "Artist" },
      { key: "must_play", label: "Must play", type: "checkbox" },
      { key: "do_not_play", label: "Do not play", type: "checkbox" },
      { key: "notes", label: "Notes", full: true, type: "textarea" },
    ]}
    columns={[{ key: "section", label: "Section" }, { key: "song", label: "Song" }, { key: "artist", label: "Artist" }, { key: "must_play", label: "Must play" }]} />;
}

export function Gifts() {
  const { wedding } = useWedding(); const cur = wedding?.currency || "AUD";
  return <RecordManager entity="gifts" title="Gifts & Thank-Yous" subtitle="Track gifts received and keep your thank-you notes on schedule." icon={Gift} emptyLabel="Add gift"
    renderCell={(c, it) => {
      if (c.key === "amount") return it.amount ? formatMoney(it.amount, cur) : "—";
      if (c.key === "thank_you_sent") return it.thank_you_sent ? "Sent ✓" : "Pending";
      return undefined;
    }}
    fields={[
      { key: "guest", label: "Guest" },
      { key: "gift", label: "Gift" },
      { key: "amount", label: "Value", type: "number" },
      { key: "date_received", label: "Date received", type: "date" },
      { key: "thank_you_sent", label: "Thank-you sent", type: "checkbox" },
      { key: "notes", label: "Notes", full: true, type: "textarea" },
    ]}
    columns={[{ key: "guest", label: "Guest" }, { key: "gift", label: "Gift" }, { key: "amount", label: "Value" }, { key: "thank_you_sent", label: "Thank-you" }]} />;
}

export function Honeymoon() {
  const { wedding } = useWedding(); const cur = wedding?.currency || "AUD";
  return <RecordManager entity="honeymoon" title="Honeymoon" subtitle="Plan and budget the getaway you've earned." icon={Plane} emptyLabel="Add expense"
    renderCell={(c, it) => (c.key === "amount" ? formatMoney(it.amount, cur) : undefined)}
    fields={[
      { key: "category", label: "Category", type: "select", options: ["Flights", "Accommodation", "Transport", "Food", "Activities", "Shopping", "Insurance", "Miscellaneous"] },
      { key: "description", label: "Description" },
      { key: "amount", label: "Amount", type: "number" },
      { key: "notes", label: "Notes", full: true, type: "textarea" },
    ]}
    columns={[{ key: "category", label: "Category" }, { key: "description", label: "Description" }, { key: "amount", label: "Amount" }]} />;
}

export function Contacts() {
  return <RecordManager entity="contacts" title="Contacts" subtitle="Your central address book for everyone involved." icon={Contact} emptyLabel="Add contact"
    fields={[
      { key: "name", label: "Name" },
      { key: "category", label: "Category", type: "select", options: ["Family", "Wedding Party", "Vendors", "Venue", "Photographer", "Planner", "Caterer", "Emergency", "Other"] },
      { key: "phone", label: "Phone" },
      { key: "email", label: "Email" },
      { key: "address", label: "Address", full: true },
      { key: "notes", label: "Notes", full: true, type: "textarea" },
    ]}
    columns={[{ key: "name", label: "Name" }, { key: "category", label: "Category" }, { key: "phone", label: "Phone" }, { key: "email", label: "Email" }]} />;
}
